import React, { useState, useEffect, useRef } from "react";
import { CModal, CButton } from "@carrefour/design-system-components-react";
import Box from "../Box";
import { useTranslation } from "react-i18next";
import { ReactComponent as FrIcon } from "../../media/francais.svg";
import { ReactComponent as EngIcon } from "../../media/english.svg";
import { ReactComponent as DutchIcon } from "../../media/dutch.svg";
import styled from "styled-components";
import Line from "../Line";

interface SelectLanguageModalProps {
  open: boolean;
  onSave: (lang: string) => void;
  onClose: () => void;
}

const CenteredModal = styled(CModal)`
  @media (max-width: 768px) {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    bottom: auto !important;
    margin: 0 !important;
    width: calc(100% - 32px) !important;
    max-width: 400px !important;
    border-radius: 8px !important;
  }
`;

const SelectLanguageModal: React.FC<SelectLanguageModalProps> = ({ open, onSave, onClose }) => {
  const { i18n } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState(
    i18n.language.slice(0, 2)
  );

  const selectedLanguageRef = useRef(selectedLanguage);

  useEffect(() => {
    if (open) {
      const lang = i18n.language.slice(0, 2);
      setSelectedLanguage(lang);
      selectedLanguageRef.current = lang;
    }
  }, [open, i18n.language]);

  const handleSelectLanguage = (lang: string) => {
    setSelectedLanguage(lang);
    selectedLanguageRef.current = lang;
  };

  const handleSaveLanguage = async () => {
    const currentLang = selectedLanguageRef.current;
    if (currentLang === i18n.language.slice(0, 2)) {
      onClose();
      return;
    }
    
    onSave(currentLang);
    onClose();
  };

  const tPreview = i18n.getFixedT(selectedLanguage);

  return (
    <CenteredModal
      show={open}
      position="center"
      onClose={() => {}}
      title={tPreview("common.select_language")}
      closable={false}
      size="s"
      footer={
        <Box width="100%">
          <Line />
          <Box display="flex" justifyContent="flex-end" style={{ gap: "16px", marginTop: "16px" }}>
            <CButton variation="secondary" onClick={onClose}>
              {tPreview("common.cancel", "Annuler")}
            </CButton>

            <CButton variation="primary" onClick={handleSaveLanguage}>
              {tPreview("common.save", "Enregistrer")}
            </CButton>
          </Box>
        </Box>
      }
    >
      <Box display="flex" justifyContent="center" style={{ padding: "24px 0" }}>
        <Box display="flex" flexDirection="column" style={{ gap: "16px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="radio"
              name="language"
              value="fr"
              checked={selectedLanguage === "fr"}
              onChange={() => handleSelectLanguage("fr")}
            />
            <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
              <FrIcon height={22} width={22} />
              <span>Français</span>
            </Box>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="radio"
              name="language"
              value="en"
              checked={selectedLanguage === "en"}
              onChange={() => handleSelectLanguage("en")}
            />
            <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
              <EngIcon height={22} width={22} />
              <span>English</span>
            </Box>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="radio"
              name="language"
              value="nl"
              checked={selectedLanguage === "nl"}
              onChange={() => handleSelectLanguage("nl")}
            />
            <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
              <DutchIcon height={22} width={22} />
              <span>Dutch</span>
            </Box>
          </label>
        </Box>
      </Box>
    </CenteredModal>
  );
};

export default SelectLanguageModal;